import { api } from "@breakout/backend/convex/_generated/api"
import { prepareBuy } from "@breakout/sdk"
import { deadlineSec } from "@breakout/shared/utils/time"
import { useWallets } from "@privy-io/react-auth"
import { useMutation } from "@tanstack/react-query"
import { useAction } from "convex/react"
import { useCallback, useState } from "react"
import type { Address, Hex } from "viem"
import { getAddress, isAddress, maxUint256 } from "viem"
import { InsufficientFundsError } from "../../lib/errors/tradeErrors"
import { useBreakoutConfig } from "../../lib/providers"
import { captureTradeError } from "../../lib/sentry"
import {
	buildPermitSigningPayload,
	DEFAULT_DEADLINE_SECONDS,
	getPermitNonce,
	PERMIT2_ADDRESS,
	serializeBuyPreparation,
	USDC_TOKEN_NAME,
} from "../../lib/tradeUtils"
import { createWalletClientFromWallet, findEmbeddedWallet } from "../../lib/wallet"
import { useInvalidateAfterTransaction } from "./useInvalidateAfterTransaction"

export type TradeStatus = "idle" | "processing" | "confirmed" | "error"

interface BuyParams {
	marketAddress: Address
	bucket: number
	/** Amount of USDC to spend (human-readable) */
	usdAmount: number
	slippageBps?: number
}

interface TradeResult {
	success: boolean
	transactionHash: string
	blockNumber?: number
}

export function useBuy() {
	const config = useBreakoutConfig()
	const { wallets } = useWallets()
	const [status, setStatus] = useState<TradeStatus>("idle")
	// Track Permit2 approval status per user to skip check on subsequent buys
	const [permit2ApprovedUsers, setPermit2ApprovedUsers] = useState<Set<string>>(new Set())
	const executeGaslessBuy = useAction(api.domains.trading.actions.executeGaslessBuy)
	const { invalidateAfterTrade } = useInvalidateAfterTransaction()

	const mutation = useMutation({
		mutationFn: async ({
			marketAddress,
			bucket,
			usdAmount,
			slippageBps,
		}: BuyParams): Promise<TradeResult> => {
			const wallet = findEmbeddedWallet(wallets)
			if (!wallet) {
				throw new Error("No wallet connected")
			}

			const walletClient = await createWalletClientFromWallet({ wallet })
			if (!isAddress(wallet.address)) {
				throw new Error("Invalid wallet address")
			}
			const userAddress = getAddress(wallet.address)

			// Check if we can skip Permit2 allowance check (user already approved)
			const skipPermit2Check = permit2ApprovedUsers.has(userAddress)

			// 1. Prepare the trade (SDK calculates optimal shares for the USD amount)
			// Now includes balance check in same RPC batch
			setStatus("processing")
			const preparation = await prepareBuy(config.publicClient, {
				market: marketAddress,
				user: userAddress,
				bucket,
				usdAmount,
				chainId: config.chainId,
				usdcAddress: config.chainConfig.usdc,
				slippageBps,
				deadlineSeconds: DEFAULT_DEADLINE_SECONDS,
				skipPermit2Check,
				includeBalance: true, // Get balance in same RPC batch
			})

			// Validate user has sufficient USDC balance (now from SDK result)
			const balanceNumber = preparation.userBalance ?? 0
			const requiredAmount = preparation.quote.total

			if (balanceNumber < requiredAmount) {
				throw new InsufficientFundsError(requiredAmount, balanceNumber)
			}

			// 2. If Permit2 approval needed, sign USDC permit (gasless approval)
			let usdcPermit: {
				owner: Address
				spender: Address
				value: string
				nonce: string
				deadline: string
				signature: Hex
			} | null = null

			if (preparation.needsPermit2Approval) {
				const usdcAddress = preparation._execution.usdcAddress

				// Only need permit nonce - token name is constant
				const permitNonce = await getPermitNonce({
					client: config.publicClient,
					usdc: usdcAddress,
					owner: userAddress,
				})

				const permitDeadline = BigInt(deadlineSec(DEFAULT_DEADLINE_SECONDS))

				// Build USDC permit signing payload (use chainId from preparation)
				const permitPayload = buildPermitSigningPayload({
					chainId: preparation.chainId,
					tokenAddress: usdcAddress,
					tokenName: USDC_TOKEN_NAME, // Constant, no RPC needed
					owner: userAddress,
					spender: PERMIT2_ADDRESS,
					value: maxUint256, // Approve max for convenience
					nonce: permitNonce,
					deadline: permitDeadline,
				})

				// Sign USDC permit (silently with embedded wallet)
				const permitSignature = await walletClient.signTypedData({
					account: userAddress,
					domain: permitPayload.domain,
					types: permitPayload.types,
					primaryType: permitPayload.primaryType,
					message: permitPayload.message,
				})

				usdcPermit = {
					owner: userAddress,
					spender: PERMIT2_ADDRESS,
					value: maxUint256.toString(),
					nonce: permitNonce.toString(),
					deadline: permitDeadline.toString(),
					signature: permitSignature,
				}
			}

			// 3. Sign Permit2 witness for the trade (silently with embedded wallet)
			const tradeSignature = await walletClient.signTypedData({
				account: userAddress,
				domain: preparation.signingPayload.domain,
				types: preparation.signingPayload.types,
				primaryType: preparation.signingPayload.primaryType,
				message: preparation.signingPayload.message,
			})

			// 4. Execute the trade via gasless relayer
			const result = await executeGaslessBuy({
				preparation: serializeBuyPreparation(preparation),
				signature: tradeSignature,
				usdcPermit,
			})

			setStatus("confirmed")
			return {
				success: true,
				transactionHash: result.txHash,
				blockNumber: result.blockNumber,
			}
		},
		onSuccess: (_, variables) => {
			const wallet = findEmbeddedWallet(wallets)
			const userAddress =
				wallet?.address && isAddress(wallet.address) ? getAddress(wallet.address) : undefined
			invalidateAfterTrade({
				marketAddress: variables.marketAddress,
				userAddress,
			})
			// Mark user as having approved Permit2 (skip check on subsequent buys)
			if (userAddress && !permit2ApprovedUsers.has(userAddress)) {
				setPermit2ApprovedUsers(prev => new Set(prev).add(userAddress))
			}
		},
		onError: (error, variables) => {
			setStatus("error")
			const wallet = findEmbeddedWallet(wallets)
			if (error instanceof Error) {
				captureTradeError(error, {
					action: "buy",
					marketAddress: variables.marketAddress,
					bucket: variables.bucket,
					usdAmount: variables.usdAmount.toString(),
					walletAddress: wallet?.address,
				})
				console.error("[useBuy]", error)
			}
		},
		onSettled: () => {
			setTimeout(() => setStatus("idle"), 2000)
		},
	})

	const reset = useCallback(() => {
		setStatus("idle")
		mutation.reset()
	}, [mutation])

	return {
		...mutation,
		status,
		reset,
	}
}
